// Officer scorecard — one reader for "profit I facilitated vs what I took" (Doug 2026-10-07).
//
// The numbers are computed in the database (finance.officer_scorecard / finance.officer_scorecard_roster,
// doors public.officer_scorecard / public.officer_scorecard_roster). This module only fetches and shapes;
// it never re-derives a number. The server decides who may see what: an officer gets only their own
// scorecard, council/finance get anyone plus the roster. A 42501 comes back as { denied: true }.
//
// Pure JS, no React — the truest panel imports it. gseat-pro cannot import bricksOS and carries a twin in
// gseat-pro/services/OfficerScorecardService.js; keep the two shapes identical.

export const COMP_TYPE_LABELS = {
  strategic_pct: '% of company gross',
  leadership_pct: 'Leadership % of account net',
  stipend: 'Captain stipend',
  retainer: 'Retainer',
  revenue_share: 'Revenue share',
  shift_pay: 'Shift pay',
  bonus: 'Bonuses',
  other_adjustment: 'Other adjustments',
  owner_draw: 'Owner draws',
  payout: 'Payouts',
  cash_kept: 'Cash kept from collections',
  ytd_net_tranche: 'Credited as % of YTD net (wrong period + rate)',
  bulk_backfill_2026_07_17: 'Bulk backfill 2026-07-17 (no payment evidence)',
};

const easternToday = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

/** Window for a mode: 'ytd' (Jan 1 → today, by month), 'month' (this month, by week), 'weeks' (last 12 weeks). */
export function scorecardWindow(mode = 'ytd', today = easternToday()) {
  const [y, m] = today.split('-');
  if (mode === 'month') return { start: `${y}-${m}-01`, end: today, grain: 'week' };
  if (mode === 'weeks') {
    const d = new Date(`${today}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 7 * 12);
    return { start: d.toISOString().slice(0, 10), end: today, grain: 'week' };
  }
  return { start: `${y}-01-01`, end: today, grain: 'month' };
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const isDenied = (error) => error && (error.code === '42501' || /not_authorized/i.test(error.message || ''));

/** Shape the long-format rows of public.officer_scorecard into what a screen renders. */
export function shapeScorecard(rows = []) {
  const totals = { facilitated_cash: 0, facilitated_accrual: 0, take: 0, paid: 0, flagged: 0 };
  const accounts = new Map();
  const take = new Map();
  const paid = new Map();
  const flags = new Map();
  const trend = new Map();
  let asOf = null;

  for (const r of rows) {
    asOf = asOf || r.as_of || null;
    const amt = num(r.amount_cash) || 0;
    if (r.row_kind === 'summary') {
      const p = trend.get(r.period_start) || { period_start: r.period_start, label: r.period_label };
      p[r.comp_type] = num(r.amount_cash);
      trend.set(r.period_start, p);
      if (r.comp_type in totals) totals[r.comp_type] += amt;
    } else if (r.row_kind === 'facilitated') {
      const a = accounts.get(r.account_id) || {
        account_id: r.account_id, account: r.account_name, roles: new Set(), revenue_cash: 0,
        contribution_cash: 0, contribution_accrual: 0, facilitated_cash: 0, facilitated_accrual: 0, shares: [],
      };
      (r.roles || '').split('+').filter(Boolean).forEach((x) => a.roles.add(x));
      a.revenue_cash += num(r.revenue_cash) || 0;
      a.contribution_cash += num(r.contribution_cash) || 0;
      a.contribution_accrual += num(r.contribution_accrual) || 0;
      a.facilitated_cash += amt;
      a.facilitated_accrual += num(r.amount_accrual) || 0;
      a.shares.push(num(r.credit_share) || 0);
      accounts.set(r.account_id, a);
    } else {
      const bucket = r.row_kind === 'take' ? take : r.row_kind === 'paid' ? paid : flags;
      bucket.set(r.comp_type, (bucket.get(r.comp_type) || 0) + amt);
    }
  }

  const toList = (m) => [...m.entries()]
    .map(([type, amount]) => ({ type, label: COMP_TYPE_LABELS[type] || type, amount }))
    .filter((x) => Math.abs(x.amount) >= 0.005)
    .sort((a, b) => b.amount - a.amount);

  return {
    asOf,
    totals: { ...totals, ratio_cash: totals.take > 0 ? totals.facilitated_cash / totals.take : null },
    accounts: [...accounts.values()]
      .map((a) => ({
        ...a,
        roles: [...a.roles].sort().join(' + '),
        credit_share: a.shares.length ? a.shares.reduce((s, x) => s + x, 0) / a.shares.length : 0,
      }))
      .sort((a, b) => b.facilitated_cash - a.facilitated_cash),
    take: toList(take),
    paid: toList(paid),
    flags: toList(flags),
    trend: [...trend.values()].sort((a, b) => String(a.period_start).localeCompare(String(b.period_start))),
  };
}

/** One person's scorecard. person null = the signed-in officer. */
export async function fetchOfficerScorecard(supabase, { person = null, start, end, grain = 'month' } = {}) {
  const { data, error } = await supabase.rpc('officer_scorecard', {
    p_person: person, p_start: start, p_end: end, p_grain: grain,
  });
  if (error) {
    if (isDenied(error)) return { denied: true };
    throw error;
  }
  return { scorecard: shapeScorecard(data || []) };
}

/** Council roster of every officer. Non-council → { denied: true }. */
export async function fetchOfficerRoster(supabase, { start, end } = {}) {
  const { data, error } = await supabase.rpc('officer_scorecard_roster', { p_start: start, p_end: end });
  if (error) {
    if (isDenied(error)) return { denied: true };
    throw error;
  }
  return {
    roster: (data || []).map((r) => ({
      ...r,
      facilitated_cash: num(r.facilitated_cash),
      facilitated_accrual: num(r.facilitated_accrual),
      take: num(r.take),
      paid: num(r.paid),
      ratio_cash: num(r.ratio_cash),
      flagged: num(r.flagged),
    })),
  };
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
export const fmtUsd = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : usd.format(Number(v)));
export const fmtRatio = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}×`);
export const fmtShare = (v) => (v === null || v === undefined ? '—' : `${Math.round(Number(v) * 100)}%`);
